import { Bell, Car, CheckCircle2, Download, FileSearch, LogIn, Mail } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { ErroApi } from '@/nucleo/auth/cliente-api';
import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader } from '@/ui/base/card';
import { CopyableText } from '@/ui/CopyableText';
import { ErrorState } from '@/ui/ErrorState';
import { Spinner } from '@/ui/Spinner';

import { useComprovante } from './apiDaInscricao';
import { baixarComprovantePdf } from './comprovantePdf';
import { ROTULO_DA_MODALIDADE, formatarDataHora, formatarPlaca } from './contrato';

/**
 * Comprovante da inscrição (`/credenciados/enviada/:protocolo`) — RN-CP-27.
 *
 * É a tela para a qual o assistente manda quem acabou de enviar, e a que o
 * cartão de entidade inscrita abre. Qualquer protocolo da inscrição abre o
 * comprovante inteiro: uma entidade, vários veículos, um protocolo cada.
 *
 * O comprovante é da conta do inscrito: sem sessão, a tela manda entrar; com
 * sessão de outra conta, o protocolo simplesmente não é encontrado (o
 * servidor não diz se ele existe).
 *
 * Sem contagem regressiva: a pessoa sai quando decide sair — quem anota o
 * protocolo à mão não pode perder a tela.
 */

const PROXIMOS_PASSOS = [
  {
    icone: Mail,
    texto: 'Você recebe um e-mail com todos os protocolos e o endereço de acompanhamento.',
  },
  {
    icone: FileSearch,
    texto: 'O DTP confere cada veículo. A triagem leva alguns dias úteis.',
  },
  {
    icone: Bell,
    texto:
      'Se faltar alguma coisa, você é avisado por e-mail, com o prazo e o que precisa ser corrigido.',
  },
] as const;

export function InscricaoEnviadaPage() {
  const { protocolo = '' } = useParams<{ protocolo: string }>();
  const comprovante = useComprovante(protocolo);

  if (comprovante.isPending) {
    return (
      <div className="mx-auto w-full max-w-2xl px-4 py-10 sm:px-6">
        <h1 className="sr-only">Comprovante da inscrição</h1>
        <Spinner size="lg" rotulo="Carregando o comprovante da sua inscrição…" />
      </div>
    );
  }

  if (comprovante.isError) {
    const erro = comprovante.error;
    if (erro instanceof ErroApi && erro.situacao === 401) {
      return (
        <div className="mx-auto w-full max-w-2xl px-4 py-10 sm:px-6">
          <ErrorState
            variant="load-failed"
            title="Entre para ver o comprovante"
            description="O comprovante fica na sua conta de inscrito. Entre com CPF e senha para vê-lo."
            primaryAction={{ label: 'Entrar', to: '/credenciados/entrar' }}
            secondaryAction={{ label: 'Ir para a página inicial', to: '/' }}
          />
        </div>
      );
    }
    if (erro instanceof ErroApi && erro.situacao === 404) {
      return (
        <div className="mx-auto w-full max-w-2xl px-4 py-10 sm:px-6">
          <ErrorState
            variant="resource-not-found"
            title="Não encontramos esta inscrição"
            description={`Nenhuma inscrição da sua conta responde pelo protocolo ${protocolo}. Confira se o número foi copiado inteiro.`}
            primaryAction={{ label: 'Ver minhas inscrições', to: '/credenciados/acompanhamento' }}
            secondaryAction={{ label: 'Ir para a página inicial', to: '/' }}
          />
        </div>
      );
    }
    return (
      <div className="mx-auto w-full max-w-2xl px-4 py-10 sm:px-6">
        <ErrorState
          variant="load-failed"
          description="Não foi possível carregar o comprovante agora. A sua inscrição não foi perdida — confira a conexão e tente de novo."
          primaryAction={{ label: 'Tentar de novo', onClick: () => void comprovante.refetch() }}
          secondaryAction={{ label: 'Ir para a página inicial', to: '/' }}
        />
      </div>
    );
  }

  const dados = comprovante.data;
  const protocolos = dados.veiculos.map((v) => v.protocolo);

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-10 sm:px-6">
      <Card className="border-success/40">
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-success/10">
            <CheckCircle2 className="h-9 w-9 text-success" aria-hidden="true" />
          </div>
          {/* `h1` à mão: `CardTitle` renderiza `h3`, e esta é a página inteira. */}
          <h1 className="text-2xl font-semibold leading-tight tracking-tight">Inscrição enviada</h1>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {dados.entidade.nome} · período {dados.ciclo.nome}. Enviada em{' '}
            {formatarDataHora(dados.enviadoEm)}. Cada veículo tem o seu protocolo: é com ele que
            você acompanha o andamento.
          </p>
        </CardHeader>

        <CardContent className="space-y-6">
          <section aria-labelledby="protocolos" className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 id="protocolos" className="text-sm font-semibold text-foreground">
                Protocolos ({protocolos.length})
              </h2>
              {protocolos.length > 1 && (
                <CopyableText
                  value={protocolos.join('\n')}
                  display="Copiar todos"
                  label="Copiar todos os protocolos"
                />
              )}
            </div>
            <ul className="space-y-3">
              {dados.veiculos.map((veiculo) => (
                <li
                  key={veiculo.protocolo}
                  className="space-y-2 rounded-xl border border-border bg-muted/30 p-4"
                >
                  <CopyableText
                    value={veiculo.protocolo}
                    label={`Copiar o protocolo ${veiculo.protocolo}`}
                    className="[&>span:first-child]:break-all [&>span:first-child]:text-lg [&>span:first-child]:font-bold [&>span:first-child]:text-foreground"
                  />
                  <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                    <Car aria-hidden="true" className="h-3.5 w-3.5" />
                    <span className="font-mono">{formatarPlaca(veiculo.placa)}</span>
                    <Badge variant="secondary">
                      {ROTULO_DA_MODALIDADE[veiculo.modalidade] ?? veiculo.modalidade}
                    </Badge>
                    <span>{veiculo.dre.nome}</span>
                  </p>
                  <ol className="list-decimal space-y-0.5 pl-5 text-sm text-muted-foreground">
                    {veiculo.escolas.map((e) => (
                      <li key={e.codigoEol}>{e.nome}</li>
                    ))}
                  </ol>
                  {veiculo.cooperativa && (
                    <p className="text-xs text-muted-foreground">
                      Cooperativa indicada: {veiculo.cooperativa.nome}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </section>

          <section aria-labelledby="proximos-passos" className="space-y-3">
            <h2 id="proximos-passos" className="text-sm font-semibold text-foreground">
              O que acontece agora
            </h2>
            <ul className="space-y-3 text-sm">
              {PROXIMOS_PASSOS.map((passo) => (
                <li key={passo.texto} className="flex gap-3">
                  <passo.icone
                    className="mt-0.5 h-5 w-5 shrink-0 text-primary"
                    aria-hidden="true"
                  />
                  <span className="leading-relaxed text-muted-foreground">{passo.texto}</span>
                </li>
              ))}
            </ul>
          </section>

          <Alert>
            <Mail className="h-4 w-4" aria-hidden="true" />
            <AlertTitle>Comprovante enviado para {dados.contatos.email}</AlertTitle>
            <AlertDescription>
              Confira a caixa de entrada e o lixo eletrônico. Resumo de verificação:{' '}
              <span className="break-all font-mono text-xs">{dados.sha256}</span>
            </AlertDescription>
          </Alert>

          <div className="flex flex-col gap-2 sm:flex-row">
            <Button asChild size="lg" className="flex-1">
              <Link to="/credenciados/acompanhamento">
                <LogIn className="h-4 w-4" aria-hidden="true" />
                Acompanhar minhas inscrições
              </Link>
            </Button>
            <Button
              type="button"
              size="lg"
              variant="outline"
              className="flex-1"
              onClick={() => baixarComprovantePdf(dados)}
            >
              <Download className="h-4 w-4" aria-hidden="true" />
              Baixar comprovante (PDF)
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default InscricaoEnviadaPage;
